import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Button } from '@americanexpress/dls-react';

/**
 * Dynamic FormSummarySection component that renders form data based on JSON configuration
 * Reads sections from formElements and displays them in tabs
 * All labels and field mappings come from the form JSON, not hardcoded
 */
const FormSummarySection = ({
  formElements,
  formSubmissionPayload,
  onEditSection,
}) => {
  // null = uninitialized; first section opens by default on first render
  const [openSections, setOpenSections] = useState(null);

  /**
   * Extract sections from form elements (assumes multi-tracker or similar structure)
   */
  const extractSections = useMemo(() => {
    if (!formElements || !Array.isArray(formElements)) {
      return [];
    }

    const sections = [];

    // Recursively find the sibling array that contains `section` markers,
    // then group siblings between markers into their respective section.
    const processNodeArray = (nodes) => {
      if (!Array.isArray(nodes) || nodes.length === 0) return;

      const hasSectionMarkers = nodes.some((n) => n && n.section);

      if (hasSectionMarkers) {
        let currentSection = null;
        nodes.forEach((node) => {
          if (!node) return;
          if (node.section) {
            // Deduplicate — merge into existing section if same label
            const existing = sections.find((s) => s.label === node.section);
            if (!existing) {
              // Extract navDefaultSection value from the section marker node
              const navSection = node.fieldDynamicDisplay?.mapCondition?.valueToCompare
                ?? node.fieldDynamicDisplay?.multiKeyValueMappings?.['formSubmissionPayload.navDefaultSection']
                ?? null;
              currentSection = {
                id: node.section,
                label: node.section,
                navSection,
                elements: [],
              };
              sections.push(currentSection);
            } else {
              currentSection = existing;
            }
          }
          if (currentSection) {
            currentSection.elements.push(node);
          }
        });
      } else {
        nodes.forEach((node) => {
          if (!node) return;
          if (node.childElements) processNodeArray(node.childElements);
          if (node.elements) processNodeArray(node.elements);
          if (node.elementContainer) processNodeArray(node.elementContainer);
        });
      }
    };

    processNodeArray(formElements);
    return sections;
  }, [formElements]);

  // Default: first section open; after user interaction, use their state
  const effectiveOpenSections = openSections !== null
    ? openSections
    : new Set(extractSections.length > 0 ? [String(extractSections[0].id)] : []);

  const toggleSection = (id) => {
    const next = new Set(effectiveOpenSections);
    if (next.has(id)) { next.delete(id); } else { next.add(id); }
    setOpenSections(next);
  };

  /**
   * Get field value from formSubmissionPayload only.
   * Returns exists=false when the key path does not exist.
   */
  const getFieldValue = (fieldPath) => {
    if (!fieldPath || !formSubmissionPayload || typeof formSubmissionPayload !== 'object') {
      return { exists: false, value: '' };
    }

    const normalizedPath = fieldPath.startsWith('formSubmissionPayload.')
      ? fieldPath.slice('formSubmissionPayload.'.length)
      : fieldPath;

    const pathCandidates = [normalizedPath];
    if (normalizedPath !== fieldPath) {
      pathCandidates.push(fieldPath);
    }

    // eslint-disable-next-line no-restricted-syntax -- desc
    for (const pathCandidate of pathCandidates) {
      const pathParts = pathCandidate.split('.');
      let current = formSubmissionPayload;
      let exists = true;

      // eslint-disable-next-line no-restricted-syntax -- desc
      for (const part of pathParts) {
        if (!current || typeof current !== 'object' || !(part in current)) {
          exists = false;
          break;
        }
        current = current[part];
      }

      if (exists) {
        return { exists: true, value: current };
      }
    }

    return { exists: false, value: '' };
  };

  /**
   * Flatten all review fields from an element tree into { label, value } objects
   */
  const collectFields = (elements) => {
    const fields = [];
    const traverse = (element) => {
      if (!element || typeof element !== 'object') return;
      if (element.fieldId && element.review) {
        const rawLabel = element.fieldLabel;
        const label = rawLabel && typeof rawLabel === 'object' ? rawLabel.label : rawLabel;
        const { exists, value } = getFieldValue(element.fieldId);
        if (!exists) return;
        // eslint-disable-next-line no-extra-parens -- desc
        const displayValue = typeof value === 'boolean' ? (value ? 'Yes' : 'No') : String(value || '');
        fields.push({ label: label || element.fieldId, value: displayValue });
        return;
      }
      [
        ...Array.isArray(element.childElements) ? element.childElements : [],
        ...Array.isArray(element.elements) ? element.elements : [],
        ...Array.isArray(element.elementContainer) ? element.elementContainer : [],
      ].forEach(traverse);
    };
    elements.forEach(traverse);
    return fields;
  };

  /**
   * Render one accordion section
   */
  const renderSection = (section) => {
    const sectionId = String(section.id);
    const isOpen = effectiveOpenSections.has(sectionId);
    const fields = collectFields(section.elements || []);

    return (
      <div
        key={sectionId}
        style={{
          border: '1px solid #d0d0d0', borderRadius: '4px', marginBottom: '8px', overflow: 'hidden',
        }}
      >
        {/* Accordion header */}
        <div
          role="button"
          tabIndex={0}
          onClick={() => toggleSection(sectionId)}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') toggleSection(sectionId); }}
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '14px 20px',
            backgroundColor: '#fff',
            borderBottom: isOpen ? '2px solid #006fcf' : '1px solid #d0d0d0',
            cursor: 'pointer',
            userSelect: 'none',
          }}
        >
          <span style={{
            display: 'flex', alignItems: 'center', gap: '10px', color: '#006fcf', fontWeight: '600', fontSize: '15px',
          }}
          >
            <span style={{ fontSize: '11px' }}>{isOpen ? '▼' : '▶'}</span>
            {section.label}
          </span>
          {onEditSection && (
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                onEditSection(section.navSection ?? section.id);
              }}
            >
              Edit
            </Button>
          )}
        </div>

        {/* Accordion content */}
        {isOpen && (
          <div style={{ padding: '20px 24px', backgroundColor: '#fafafa' }}>
            {fields.length > 0 ? (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px 32px' }}>
                {fields.map((field) => (
                  <div key={field.label} style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <span style={{
                      fontSize: '12px', color: '#888', fontWeight: '500', textTransform: 'uppercase', letterSpacing: '0.4px',
                    }}
                    >
                      {field.label}
                    </span>
                    <span style={{ fontSize: '14px', color: '#333', wordBreak: 'break-word' }}>
                      {field.value || 'N/A'}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ fontSize: '14px', color: '#888' }}>No review fields configured for this section.</div>
            )}
          </div>
        )}
      </div>
    );
  };

  if (!formElements || formElements.length === 0) {
    return <div style={{ padding: '24px' }}>No form data available</div>;
  }

  if (extractSections.length === 0) {
    return <div style={{ padding: '24px' }}>No sections found in form configuration</div>;
  }

  return (
    <div style={{ marginBottom: '24px' }}>
      {extractSections.map((section) => renderSection(section))}
    </div>
  );
};

FormSummarySection.propTypes = {
  formElements: PropTypes.arrayOf(
    PropTypes.shape({
      // eslint-disable-next-line react/forbid-prop-types -- desc
      childElements: PropTypes.array,
    })
  ),
  formSubmissionPayload: PropTypes.shape({}),
  onEditSection: PropTypes.func,
};

FormSummarySection.defaultProps = {
  formElements: [],
  formSubmissionPayload: null,
  onEditSection: null,
};

export default FormSummarySection;
